/**
 * The shell: chrome, the filter bar, and whichever view is mounted.
 *
 * The filter bar is not part of any view. It sits above all six of them and
 * writes to one `Filter` object, so narrowing to a person in the grid and then
 * switching to the timeline shows that person's timeline. That is the whole
 * design of this client, and the reason the bar lives here rather than being
 * drawn six times.
 *
 * What renders is decided by `/stats`'s `can` list. A contributor is not shown
 * a disabled owner control; they are shown an archive that has no such control
 * in it. Nothing in this client is expected to come back 403.
 */

import { h, replace, clear, debounce } from "./dom";
import { icon } from "./icons";
import { api, cropUrl, liveUrl, DEMO } from "./api";
import {
  store,
  SECTIONS,
  sectionOf,
  type ViewName,
} from "./state";
import { working } from "./progress";
import { ask, pickOption, pickPeople, progressModal } from "./modal";
import { filesFromDrop, uploadAll } from "./upload";
import { choosePhotos, fileIntoAlbum, type Chosen } from "./addphotos";
import { processFaces, type Progress } from "./pipeline";
import { toast } from "./ui";
import { loginScreen } from "./login";
import { openSharing } from "./sharing";
import { count, personName } from "./format";
import { lightboxOpen, closeAsset } from "./lightbox";
import type { LiveMessage, PersonSummary } from "./types";
import type { View } from "./views/view";

import * as cards from "./views/cards";
import * as dates from "./views/dates";
import * as timeline from "./views/timeline";
import * as people from "./views/people";
import * as tree from "./views/tree";
import * as albums from "./views/albums";
import * as dupes from "./views/dupes";
import * as review from "./views/review";
import * as reels from "./views/reels";
import * as reelEditor from "./views/reel-editor";

const MOUNTS: Record<ViewName, (pane: HTMLElement) => View> = {
  cards: cards.mount,
  dates: dates.mount,
  timeline: timeline.mount,
  people: people.mount,
  tree: tree.mount,
  albums: albums.mount,
  dupes: dupes.mount,
  review: review.mount,
  reels: reels.mount,
  "reel-editor": reelEditor.mount,
};



const root = document.getElementById("root")!;

// --- theme ----------------------------------------------------------------

const THEMES = ["noir", "oxide", "ember", "daylight"] as const;

function setTheme(name: string): void {
  document.documentElement.setAttribute("data-theme", name);
  try {
    localStorage.setItem("heirloom.theme", name);
  } catch {
    /* the default theme is fine */
  }
}

try {
  const saved = localStorage.getItem("heirloom.theme");
  if (saved && (THEMES as readonly string[]).includes(saved)) setTheme(saved);
} catch {
  /* ignore */
}

// --- live -----------------------------------------------------------------

let socket: WebSocket | null = null;
let socketTimer: ReturnType<typeof setTimeout> | undefined;

const markLive = (_on: boolean): void => {};

/**
 * Refreshes are debounced because an import announces one message per
 * photograph, and a thousand of those must be one repaint and not a thousand.
 */
const refreshSoon = debounce(400, () => quietRefresh());

/**
 * A live update, applied without disturbing whoever is reading.
 *
 * Held while somebody is typing into the view — a name half-entered on the
 * People screen is not something to redraw out from under them — and applied
 * the moment they leave the field.
 */
function quietRefresh(): void {
  const active = document.activeElement;
  const typing =
    active instanceof HTMLElement &&
    pane?.contains(active) &&
    (active.matches("input, textarea, select") || active.isContentEditable);
  if (typing) {
    active.addEventListener("focusout", () => refreshSoon(), { once: true });
    return;
  }
  current?.refresh({ quiet: true });
}

function connectLive(libraryId: string): void {
  if (DEMO) {
    markLive(false);
    return;
  }
  socket?.close();
  clearTimeout(socketTimer);
  let ws: WebSocket;
  try {
    ws = new WebSocket(liveUrl(libraryId));
  } catch {
    markLive(false);
    return;
  }
  socket = ws;
  ws.onopen = () => markLive(true);
  ws.onclose = () => {
    markLive(false);
    if (socket === ws) socketTimer = setTimeout(() => connectLive(libraryId), 4000);
  };
  ws.onerror = () => markLive(false);
  ws.onmessage = (ev) => {
    let msg: LiveMessage;
    try {
      msg = JSON.parse(String(ev.data)) as LiveMessage;
    } catch {
      return;
    }
    const view = store.route.view;
    const touches: Record<LiveMessage["type"], ViewName[]> = {
      asset: ["cards", "dates", "timeline", "albums", "people"],
      identity: ["people", "tree", "cards"],
      tree: ["tree", "people"],
      album: ["albums", "cards"],
      annotation: ["timeline", "people", "cards"],
      events: ["cards", "dates", "timeline", "people", "tree", "albums"],
    };
    if (msg.type === "identity" || msg.type === "tree") peopleCache = null;
    if (touches[msg.type]?.includes(view)) refreshSoon();
    if (msg.type === "identity" || msg.type === "events") void refreshPeopleBar();
    // Photographs added elsewhere — another device, or a relative's phone —
    // get found by whichever tab is open, without anybody asking it to.
    //
    // `identity` too, because naming somebody is new evidence: the server
    // offers back every photograph holding a face nobody has placed, and this
    // is what goes and looks at them. Naming a person on their fifth
    // photograph is how they get found on the other forty.
    if (msg.type === "identity") peopleCorrected();
    else if (msg.type === "asset" || msg.type === "events") sweepSoon();
  };
}

// --- chrome ---------------------------------------------------------------

let current: View | null = null;
let currentName: ViewName | null = null;
let pane: HTMLElement | null = null;
let peopleBar: HTMLElement | null = null;
let narrowedLine: HTMLElement | null = null;
let filterBar: HTMLElement | null = null;
let peopleCache: PersonSummary[] | null = null;
let railCollapsed = localStorage.getItem("heirloom.rail-collapsed") === "true";

const nameOf = (id: string): string => {
  const found = peopleCache?.find((p) => p.identityId === id);
  // The strip may not have loaded yet, and an id is not a name to show anybody.
  if (!found) return "one person";
  return found.name?.trim() ? found.name : personName(null).toLowerCase();
};

const accountName = (): string => {
  const personal = store.me?.libraries.find((library) => library.kind === "personal");
  const archiveName = personal?.name.match(/^(.+?)(?:'s|’s) archive$/i)?.[1]?.trim();
  if (archiveName) return archiveName;
  const local = store.me?.account.email.split("@")[0] ?? "Account";
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part[0]!.toUpperCase() + part.slice(1))
    .join(" ");
};

const openAccountMenu = (): void => {
  const email = store.me?.account.email ?? "";
  void pickOption({
    title: accountName(),
    choices: [
      { value: "account", label: "Account", detail: email || "Profile and security", icon: "user" },
      { value: "billing", label: "Billing", detail: "Plans and storage", icon: "card" },
      { value: "signout", label: "Sign out", detail: "Sign out of Heirloom", icon: "logout" },
    ],
  }).then((choice) => {
    if (choice === "account") toast("Account settings are coming soon.");
    else if (choice === "billing") toast("Billing settings are coming soon.");
    else if (choice === "signout") {
      if (DEMO) toast("Sign out is unavailable in the demo.");
      else void api.signOut().finally(() => location.reload());
    }
  });
};

/**
 * The rail.
 *
 * Down the side rather than across the top because it holds subjects, not
 * ways of looking: the artifacts, the people, the film. Those stay put, keep
 * their number key, and do not move as labels change length.
 *
 * It was briefly folded into the view picker, which flattened the two levels
 * into one list. That was wrong in the other direction — People and Film are
 * not ways of looking at the photographs, they are different things to look
 * at, and burying them in a dropdown meant you had to open a menu to discover
 * the archive had people in it.
 */
function rail(): HTMLElement {
  const nav = h("nav", { class: "rail", "aria-label": "Sections" });
  nav.appendChild(
    h(
      "div",
      { class: "rail-brand-head" },
      h("div", { class: "brand" }, h("span", { class: "mark" }, icon("album")), "Heirloom"),
      h(
        "button",
        {
          class: "ib rail-collapse",
          title: railCollapsed ? "Expand sidebar" : "Collapse sidebar",
          "aria-label": railCollapsed ? "Expand sidebar" : "Collapse sidebar",
          "aria-expanded": String(!railCollapsed),
          onclick: (event: Event) => {
            railCollapsed = !railCollapsed;
            localStorage.setItem("heirloom.rail-collapsed", String(railCollapsed));
            document.querySelector(".frame")?.classList.toggle("rail-collapsed", railCollapsed);
            const button = event.currentTarget as HTMLButtonElement;
            const label = railCollapsed ? "Expand sidebar" : "Collapse sidebar";
            button.title = label;
            button.setAttribute("aria-label", label);
            button.setAttribute("aria-expanded", String(!railCollapsed));
          },
        },
        icon("chevron"),
      ),
      h(
        "button",
        {
          class: "ib rail-close",
          title: "Close menu",
          "aria-label": "Close menu",
          onclick: () => setMobileMenu(false),
        },
        icon("x"),
      ),
    ),
  );
  const mobileLibrary = libraryPicker("rail-library");
  if (mobileLibrary) nav.appendChild(mobileLibrary);
  const here = sectionOf(store.route.view);
  SECTIONS.forEach((sec, i) => {
    nav.appendChild(
      h(
        "button",
        {
          class: sec.name === here.name ? "on" : "",
          "data-section": sec.name,
          title: `${sec.label} — press ${i + 1}`,
          onclick: () => {
            setMobileMenu(false);
            store.go(sec.views[0]!, sec.name === "people" ? { personId: null } : {});
          },
        },
        icon(sec.icon, "i"),
        h("span", { class: "rail-label", text: sec.label }),
        // No number badge. It was the keyboard shortcut, but sitting on the
        // right of a nav row next to "People" it read as a count of people —
        // a number with no units that changed nothing when you looked at it.
        // The shortcut still works and the tooltip still names it.
      ),
    );
  });
  const profileName = accountName();
  const email = store.me?.account.email ?? "";
  nav.appendChild(
    h(
      "button",
      {
        class: "rail-account",
        type: "button",
        title: `${profileName}${email ? ` — ${email}` : ""}`,
        "aria-label": `Open account menu for ${profileName}`,
        onclick: () => openAccountMenu(),
      },
      h("span", { class: "rail-avatar", text: profileName.slice(0, 1).toUpperCase() }),
      h(
        "span",
        { class: "rail-account-copy" },
        h("strong", { text: profileName }),
        email ? h("small", { text: email }) : null,
      ),
      icon("chevron", "i sm rail-account-chevron"),
    ),
  );
  nav.appendChild(
    h(
      "div",
      { class: "rail-mobile-foot" },
      DEMO ? h("span", { class: "libstat", text: "Demo data" }) : null,
    ),
  );
  return nav;
}

function setMobileMenu(open: boolean): void {
  document.querySelector(".frame")?.classList.toggle("mobile-menu-open", open);
  document.querySelector<HTMLButtonElement>(".mobile-menu-button")?.setAttribute("aria-expanded", String(open));
}

function libraryPicker(className = ""): HTMLElement | null {
  const libs = store.me?.libraries ?? [];
  if (!libs.length) return null;
  return h(
    "div",
    { class: `field libpick ${className}`.trim(), title: "Switch library" },
    icon("swap", "i sm"),
    h(
      "select",
      { onchange: (e: Event) => store.setLibrary((e.target as HTMLSelectElement).value) },
      ...libs.map((library) =>
        h("option", {
          value: library.id,
          selected: library.id === store.libraryId,
          text: library.name,
        }),
      ),
    ),
  );
}

/**
 * Ways of looking within the section you are already on.
 *
 * Both widgets are rendered and CSS shows one. On a desktop the views are a
 * visible strip. A list of two or four short labels costs a row of pixels a
 * desktop has and answers "what can I do here" without being opened.
 *
 * The dropdown survives for narrow viewports, where it was right for the
 * reason it was chosen: four labels are the widest thing in the bar and the
 * first to wrap on a phone, and a `<select>` there is a native wheel.
 *
 * Nothing is rendered for a section with one view; a picker that cannot pick
 * anything is a control in the way.
 */
function header(): HTMLElement {
  return h("header", null, filters(), narrowed());
}

// --- the filter bar -------------------------------------------------------

const viewMenuIcon = (view: ViewName | "gallery" | "masonry" | "list"): string => ({
  gallery: "grid",
  masonry: "masonry",
  list: "list",
  cards: "grid",
  dates: "calendar",
  timeline: "timeline",
  people: "people",
  tree: "tree",
  albums: "album",
  dupes: "album",
  review: "eye",
  reels: "play",
  "reel-editor": "film",
})[view];

function filters(): HTMLElement {
  const f = store.filter;
  const artifactTools = sectionOf(store.route.view).name === "artifacts";
  const toolAttrs = { "data-artifact-tool": "", hidden: artifactTools ? undefined : "" };

  const search = h("input", {
    type: "search",
    placeholder: "Search captions, descriptions, names",
    value: f.q ?? "",
    "data-role": "q",
  });
  search.addEventListener(
    "input",
    debounce(260, () => store.setFilter({ q: search.value.trim() || undefined })),
  );

  const dateButton = h("button", {
    class: f.from || f.to ? "ib on" : "ib",
    type: "button",
    title: "Filter by date",
    "aria-label": "Filter by date",
    "data-role": "date-filter",
    onclick: () => {
      void ask({
        title: "Filter by date",
        fields: [
          { name: "from", label: "From", value: store.filter.from ?? "", inputType: "date" },
          { name: "to", label: "To", value: store.filter.to ?? "", inputType: "date" },
        ],
        confirm: "Apply dates",
      }).then((answer) => {
        if (answer) store.setFilter({ from: answer.from || undefined, to: answer.to || undefined });
      });
    },
  }, icon("calendar"));

  const formatButton = h("button", {
    class: f.kind ? "ib on" : "ib",
    type: "button",
    title: "Filter by file format",
    "aria-label": "Filter by file format",
    "data-role": "format-filter",
    onclick: () => {
      void pickOption({
        title: "File format",
        selected: store.filter.kind ?? "all",
        choices: [
          { value: "all", label: "All artifacts", icon: "album" },
          { value: "image", label: "Photographs", icon: "image" },
          { value: "video", label: "Video", icon: "film" },
        ],
      }).then((value) => {
        if (value) store.setFilter({ kind: value === "all" ? undefined : value as "image" | "video" });
      });
    },
  }, icon("image"));

  const currentView = store.route.view === "cards" ? store.cardLayout : store.route.view;
  const viewButton = h("button", {
    class: "ib",
    type: "button",
    title: "Choose view",
    "aria-label": "Choose view",
    "data-role": "view-menu",
    onclick: () => {
      void pickOption({
        title: "View artifacts",
        selected: store.route.view === "cards" ? store.cardLayout : store.route.view,
        choices: [
          { value: "gallery", label: "Gallery", detail: "Even rows for quick scanning", icon: "grid" },
          { value: "masonry", label: "Masonry grid", detail: "Keep each artifact's proportions", icon: "masonry" },
          { value: "list", label: "List", detail: "Compact rows", icon: "list" },
          { value: "dates", label: "Date groups", detail: "Browse by month, year, or decade", icon: "calendar" },
          { value: "timeline", label: "Timeline", detail: "Arrange artifacts through time", icon: "timeline" },
          { value: "tree", label: "Family tree", detail: "Arrange the people in this archive", icon: "tree" },
          { value: "albums", label: "Albums", detail: "Hand-picked collections", icon: "album" },
          { value: "dupes", label: "Duplicates", detail: "Review likely duplicate artifacts", icon: "album" },
          { value: "review", label: "Waiting for you", detail: "Decisions that need attention", icon: "eye" },
        ],
      }).then((value) => {
        if (!value) return;
        if (["dates", "timeline", "tree", "albums", "dupes", "review"].includes(value)) {
          store.go(value as ViewName);
        }
        else {
          store.setCardLayout(value as "gallery" | "masonry" | "list");
          store.go("cards");
        }
      });
    },
  }, icon(viewMenuIcon(currentView)));

  const artifactTool = <T extends HTMLElement>(element: T): T => {
    element.dataset.artifactTool = "";
    element.hidden = !artifactTools;
    return element;
  };

  const bar = h(
    "div",
    { class: "bar filters toolbar", role: "toolbar", "aria-label": "Archive tools" },
    h(
      "button",
      {
        class: "ib mobile-menu-button",
        title: "Open menu",
        "aria-label": "Open menu",
        "aria-expanded": "false",
        onclick: () => setMobileMenu(true),
      },
      icon("menu"),
    ),
    h("div", { class: "field searchf", ...toolAttrs }, icon("search"), search),
    store.can("asset.add")
      ? h("button", {
          class: "ib primary toolbar-upload toolbar-upload-mobile",
          title: "Upload photographs",
          "aria-label": "Upload photographs",
          onclick: () => pickPhotographs(),
        }, icon("upload"))
      : null,
    store.can("reel.render")
      ? h("button", {
          class: "btn sm primary toolbar-reel",
          type: "button",
          hidden: sectionOf(store.route.view).name !== "film" || store.route.view === "reel-editor" ? "" : undefined,
          "data-reel-tool": "",
          onclick: () => store.go("reel-editor"),
        }, icon("film", "i sm"), h("span", { text: "Make a reel" }))
      : null,
    artifactTool(dateButton),
    artifactTool(peopleBarEl()),
    artifactTool(formatButton),
    artifactTool(viewButton),
    h("div", { class: "grow" }),
    store.narrowed
      ? h("button", {
          class: "ib clear-filter",
          title: "Clear filters",
          "aria-label": "Clear filters",
          "data-artifact-tool": "",
          hidden: artifactTools ? undefined : "",
          onclick: () => store.clearFilter(),
        }, icon("x"))
      : null,
    store.can("share.create")
      ? h("button", {
          class: "ib toolbar-share",
          type: "button",
          title: "Share this archive",
          "aria-label": "Manage sharing",
          onclick: () => void openSharing(),
        }, icon("share"))
      : null,
    store.can("asset.add")
      ? h("button", {
          class: "ib primary toolbar-upload toolbar-upload-desktop",
          title: "Upload photographs",
          "aria-label": "Upload photographs",
          onclick: () => pickPhotographs(),
        }, icon("upload"))
      : null,
  );
  filterBar = bar;
  return bar;
}

/** Update the bar in place, so typing in the search box is never interrupted. */
function syncFilters(): void {
  if (!filterBar) return;
  const f = store.filter;
  const q = filterBar.querySelector<HTMLInputElement>('[data-role="q"]');
  if (q && document.activeElement !== q) q.value = f.q ?? "";
  filterBar.querySelector('[data-role="date-filter"]')?.classList.toggle("on", !!(f.from || f.to));
  filterBar.querySelector('[data-role="format-filter"]')?.classList.toggle("on", !!f.kind);

  const clear = filterBar.querySelector<HTMLButtonElement>(".clear-filter");
  if (store.narrowed && !clear) {
    filterBar.insertBefore(
      h("button", {
        class: "ib clear-filter",
        title: "Clear filters",
        "aria-label": "Clear filters",
        onclick: () => store.clearFilter(),
      }, icon("x")),
      filterBar.querySelector(".toolbar-upload"),
    );
  } else if (!store.narrowed && clear) {
    clear.remove();
  }
}

// --- the people strip -----------------------------------------------------

function peopleBarEl(): HTMLElement {
  peopleBar = h("span", { class: "peoplebar" });
  void refreshPeopleBar();
  return peopleBar;
}

async function refreshPeopleBar(): Promise<void> {
  if (!peopleBar) return;
  if (!peopleCache) {
    try {
      peopleCache = await api.people(store.libraryId);
    } catch {
      peopleCache = [];
    }
    // The summary line names people, so it has to be redrawn once it can.
    syncNarrowed();
  }
  if (!peopleBar) return;
  const chosen = new Set(store.filter.people ?? []);
  const selectedPeople = chosen.size === 1 ? "1 person selected" : `${chosen.size} people selected`;

  clear(peopleBar);
  if (!peopleCache.length) return;
  peopleBar.appendChild(
    h(
      "button",
      {
        class: chosen.size ? "ib on" : "ib",
        title: chosen.size ? `Filter by people: ${selectedPeople}` : "Filter by people",
        "aria-label": chosen.size ? `Filter by people, ${selectedPeople}` : "Filter by people",
        onclick: () => {
          void pickPeople({
            title: "Photographs with people",
            choices: peopleCache!.map((person) => ({
              value: person.identityId,
              label: personName(person.name),
              detail: count(person.count, "photograph"),
              image: cropUrl(person.coverCrop),
            })),
            selected: [...chosen],
            multiple: true,
            confirm: "Show photographs",
          }).then((selected) => {
            if (selected) store.setFilter({ people: selected.length ? selected : undefined });
          });
        },
      },
      icon("people"),
    ),
  );
}

// --- the narrowing summary ------------------------------------------------

function narrowed(): HTMLElement {
  narrowedLine = h("div", { class: "narrowed" });
  syncNarrowed();
  return narrowedLine;
}

function syncNarrowed(): void {
  if (!narrowedLine) return;
  clear(narrowedLine);
  const parts = store.describe(nameOf);
  if (!parts.length) {
    narrowedLine.style.display = "none";
    return;
  }
  narrowedLine.style.display = "";
  narrowedLine.appendChild(h("span", { text: "Showing only:" }));
  for (const p of parts) narrowedLine.appendChild(h("b", { text: p }));
  narrowedLine.appendChild(
    h("span", {
      class: "hint",
      text: "· this narrowing follows you between every view",
    }),
  );
}

// --- mounting -------------------------------------------------------------

function mountView(): void {
  if (!pane) return;
  const name = store.route.view;
  if (name === currentName && current) {
    current.refresh();
    return;
  }
  current?.destroy?.();
  clear(pane);
  pane.scrollTop = 0;
  currentName = name;
  current = MOUNTS[name](pane);
  for (const b of document.querySelectorAll<HTMLElement>("[data-tab]")) {
    b.classList.toggle("on", b.dataset["tab"] === name);
  }
}

// --- adding photographs ---------------------------------------------------

/**
 * The one path into the archive from a browser.
 *
 * Dropping a folder is what people actually do with a box of scans, so the
 * whole window is a drop target rather than a small rectangle somebody has to
 * find first. The button exists as well because drag-and-drop is
 * undiscoverable and cannot be done from a keyboard at all.
 */
let processing = false;
let stopProcessing = false;

/**
 * Find the people, then queue the descriptions.
 *
 * Shown, and out of the way. It fetches 63 MB of model on first use and then
 * works through the archive a photograph at a time, so a silent version of
 * this is one the user cannot tell has stalled — but it also runs for minutes,
 * and the modal it used to put up meant minutes of not being able to name
 * anybody, open a photograph or look at an album, on the archive the pass is
 * in the middle of improving. A ring in the corner says the same thing without
 * taking the window.
 */
/**
 * Whether somebody has asked this session to stop working.
 *
 * A pass that starts itself must also stay stopped. Without this, pressing
 * Stop would end one pass and the next sweep would start another, which is
 * not a Stop button, it is a pause that ignores you.
 */
let sweepDeclined = false;

/**
 * A sweep was asked for while one was already working.
 *
 * Dropping it was the bug behind naming appearing to do nothing: the first
 * name starts a pass, the second, third and fourth arrive while it runs and
 * were thrown away, and the photographs they offered back were stamped with
 * answers from before any of them.
 */
let sweepQueued = false;

/** A correction landed since the running pass loaded its exemplars. */
let evidenceDirty = false;

/** People changed since the same-person comparison last looked. */
let peopleChanged = false;

/**
 * Somebody named, merged, placed or rejected a face — here or on another
 * device. The desktop re-matches after every one of those, so this does too.
 */
function peopleCorrected(): void {
  evidenceDirty = true;
  peopleChanged = true;
  sweepSoon();
}

/**
 * Start a pass if there is one to run.
 *
 * Called after boot, after an upload, and when the live socket says the
 * archive changed — so photographs added from a phone are found by the
 * desktop tab that is already open, and an interrupted pass resumes by
 * itself rather than waiting to be noticed.
 *
 * It asks the server for one pending photograph before loading 63 MB of
 * model. Nothing is downloaded for an archive that is already done, which is
 * the common case every time somebody opens the tab.
 */
async function sweep(): Promise<void> {
  if (sweepDeclined || DEMO) return;
  if (!store.can("asset.add")) return;
  if (processing) {
    sweepQueued = true;
    return;
  }
  let waiting: boolean;
  try {
    const pending = await api.cards(store.libraryId, { facesPending: true }, { limit: 1 });
    waiting = pending.items.length > 0;
  } catch {
    // Not worth a message. If the archive is unreachable the views are
    // already saying so.
    return;
  }
  if (waiting) await runProcessing();
  else if (peopleChanged) await comparePeople();
}

/** Whatever was asked for while the last piece of work ran. */
function sweepIfQueued(): void {
  if (!sweepQueued && !peopleChanged) return;
  sweepQueued = false;
  void sweep();
}

/**
 * "Is this the same person?", asked without a button.
 *
 * The browser half of the desktop's relearn. Naming a group does not change
 * what any face is matched against — every group's crops were already
 * exemplars — so re-running the pass alone found nothing new, and the name
 * seemed to go nowhere. What a name *does* make worth doing is holding the
 * other unnamed groups up against that person. Found pairs go to the queue;
 * nothing is merged without somebody agreeing.
 */
async function comparePeople(): Promise<void> {
  if (!store.can("identity.name")) {
    peopleChanged = false;
    return;
  }
  peopleChanged = false;
  processing = true;
  try {
    const { findSimilarPeople } = await import("./similar");
    const r = await findSimilarPeople();
    if (r.suggested) {
      toast(
        `${count(r.suggested, "possible match", "possible matches")} to look at ` +
          "in Waiting for you.",
      );
      if (store.route.view === "review") current?.refresh({ quiet: true });
    }
  } catch (e) {
    // Background work. The same comparison is one button away in the queue,
    // where a failure is worth reading.
    console.warn("same-person comparison failed:", e);
  } finally {
    processing = false;
    sweepIfQueued();
  }
}

/** Debounced: an import announces one live message per photograph. */
/**
 * Debounced: an import announces one live message per photograph, and naming
 * four people in one lightbox announces four.
 *
 * Long enough that naming several people in a row is one pass rather than
 * four, short enough that it feels like a consequence of what you just did.
 */
const sweepSoon = debounce(2500, () => void sweep());

async function runProcessing(): Promise<void> {
  if (processing) {
    toast("Already working through them.");
    return;
  }
  if (!store.can("asset.add")) {
    toast("You can look at this archive but not change it.", true);
    return;
  }
  processing = true;
  stopProcessing = false;
  // Whatever arrived before now is already in the exemplars this pass loads.
  evidenceDirty = false;

  const box = working("Finding the people", () => {
    stopProcessing = true;
    // And do not start another one behind their back.
    sweepDeclined = true;
  });
  const show = (p: Progress): void => {
    if (p.stage === "loading models") {
      box.update(p.note ?? "loading the models", 0, 1);
      return;
    }
    box.update(
      `${p.done} of ${p.total}` +
        (p.faces ? ` — ${count(p.faces, "face")}, ${p.named} named` : "") +
        (p.note ? ` — ${p.note}` : ""),
      p.done,
      p.total,
    );
  };

  try {
    const r = await processFaces({
      onProgress: show,
      cancelled: () => stopProcessing,
      evidenceChanged: () => {
        const changed = evidenceDirty;
        evidenceDirty = false;
        return changed;
      },
    });
    box.close();
    const parts = [`looked at ${count(r.done, "photograph")}`];
    if (r.faces) parts.push(`${count(r.faces, "face")} found`);
    if (r.named) parts.push(`${r.named} recognised`);
    if (r.groups) parts.push(`${count(r.groups, "new person", "new people")} to name`);
    if (r.unreadable) {
      parts.push(`${count(r.unreadable, "file")} could not be read`);
    }
    if (r.stage === "queued") parts.push("descriptions queued");
    toast(parts.join(" · "));
    void loadStats().then(shell);
  } catch (e) {
    box.close();
    // The message matters here: "could not load the inference runtime" and
    // "the model does not match its checksum" need different responses.
    toast(e instanceof Error ? e.message : "the pass failed", true);
  } finally {
    processing = false;
    if (!stopProcessing) sweepIfQueued();
  }
}

async function runUpload(chosen: Chosen): Promise<void> {
  if (!chosen.files.length) return;
  const box = progressModal(`Adding ${count(chosen.files.length, "photograph")}`);
  try {
    const r = await uploadAll(store.libraryId, chosen.files, {
      onProgress: (done, total, current) =>
        box.update(`${done} of ${total} — ${current}`, done, total),
      onFailure: (name, why) => box.fail(`${name}: ${why}`),
    });
    const filed = await fileIntoAlbum(chosen, r.assetIds);
    box.close();

    const parts: string[] = [];
    if (r.added) parts.push(`added ${count(r.added, "photograph")}`);
    // Said out loud rather than silently doing nothing: re-importing a folder
    // somebody has added four pictures to otherwise looks broken.
    if (r.deduplicated) parts.push(`${r.deduplicated} already here`);
    if (r.failed.length) parts.push(`${r.failed.length} could not be read`);
    if (filed) parts.push(filed);
    toast(parts.join(" · ") || "nothing to add", r.added === 0 && r.failed.length > 0);
    if (r.added || r.deduplicated) void loadStats().then(shell);
    // Straight into processing. Adding photographs and then having to ask for
    // them to be looked at is two steps where the user only ever wants one.
    if (r.added) void runProcessing();
  } catch (e) {
    box.close();
    toast(e instanceof Error ? e.message : "the upload failed", true);
  }
}

/** The button, and the destination question that goes with it. */
function pickPhotographs(preselected?: File[]): void {
  if (!store.can("asset.add")) {
    toast("You can look at this archive but not add to it.", true);
    return;
  }
  void choosePhotos(preselected).then((chosen) => {
    if (chosen) void runUpload(chosen);
  });
}

/**
 * A drop goes through the same question.
 *
 * Tempting to upload immediately — the files are right there — but then a drop
 * and a click behave differently and the album choice is only available one of
 * those ways. The files are pre-filled, so the dialog is one confirmation
 * rather than a repeat of the choosing.
 */
function addPhotographs(files: readonly File[]): void {
  if (!files.length) return;
  pickPhotographs([...files]);
}

/** Whole-window drop, showing a target only while something is over it. */
function wireDrop(): void {
  let depth = 0;
  const veil = h(
    "div",
    { class: "dropveil", hidden: "" },
    h(
      "div",
      { class: "dropveil-inner" },
      h("strong", { text: "Add these to the archive" }),
      h("span", { text: "Folders are walked through. Nothing is uploaded twice." }),
    ),
  );
  document.body.appendChild(veil);
  const show = (on: boolean): void => {
    veil.hidden = !on;
  };

  // dragenter and dragleave fire for every child element the cursor crosses,
  // so a plain toggle flickers constantly. Counting depth does not.
  document.addEventListener("dragenter", (e) => {
    if (!(e as DragEvent).dataTransfer?.types.includes("Files")) return;
    depth++;
    show(store.can("asset.add"));
  });
  document.addEventListener("dragleave", () => {
    depth = Math.max(0, depth - 1);
    if (!depth) show(false);
  });
  document.addEventListener("dragover", (e) => {
    if ((e as DragEvent).dataTransfer?.types.includes("Files")) e.preventDefault();
  });
  document.addEventListener("drop", (e) => {
    const dt = (e as DragEvent).dataTransfer;
    if (!dt?.types.includes("Files")) return;
    e.preventDefault();
    depth = 0;
    show(false);
    void filesFromDrop(dt).then(addPhotographs);
  });
}

/**
 * Keep the rail and the mode strip pointing at where you actually are.
 *
 * A route change only re-mounts the view — re-rendering the whole shell would
 * throw away the filter bar's focus and scroll along with it. So the chrome is
 * updated in place instead. Without this the rail highlights whichever section
 * was open when the page loaded, for ever, which is worse than having no
 * highlight at all: it points confidently at the wrong place.
 */
function syncNav(): void {
  const here = sectionOf(store.route.view);
  for (const b of document.querySelectorAll<HTMLElement>(".rail [data-section]")) {
    b.classList.toggle("on", b.dataset.section === here.name);
  }

  const viewButton = filterBar?.querySelector<HTMLElement>('[data-role="view-menu"]');
  const currentView = store.route.view === "cards" ? store.cardLayout : store.route.view;
  viewButton?.querySelector("svg")?.replaceWith(icon(viewMenuIcon(currentView)));
  for (const tool of filterBar?.querySelectorAll<HTMLElement>("[data-artifact-tool]") ?? []) {
    tool.hidden = here.name !== "artifacts";
  }
  for (const tool of filterBar?.querySelectorAll<HTMLElement>("[data-reel-tool]") ?? []) {
    tool.hidden = store.route.view !== "reels";
  }
}

function shell(): void {
  pane = h("div", { class: "pane" });
  replace(
    root,
    h(
      "div",
      { class: railCollapsed ? "frame rail-collapsed" : "frame" },
      rail(),
      h("button", {
        class: "menuveil",
        type: "button",
        "aria-label": "Close menu",
        onclick: () => setMobileMenu(false),
      }),
      h("div", { class: "column" }, header(), h("main", null, pane)),
    ),
  );
  currentName = null;
  current = null;
  mountView();
}

// --- keyboard -------------------------------------------------------------

function typing(): boolean {
  const el = document.activeElement;
  return (
    !!el &&
    (el.tagName === "INPUT" || el.tagName === "SELECT" || el.tagName === "TEXTAREA")
  );
}

document.addEventListener("keydown", (e) => {
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  if (e.key === "Escape") {
    const dialog = document.querySelector<HTMLDialogElement>("dialog[open]");
    if (dialog) {
      e.preventDefault();
      dialog.dispatchEvent(new Event("cancel", { cancelable: true }));
      return;
    }
    if (lightboxOpen()) closeAsset();
    else if (typing()) (document.activeElement as HTMLElement).blur();
    else store.clearFilter();
    return;
  }
  if (typing()) return;
  if (e.key === "/") {
    e.preventDefault();
    filterBar?.querySelector<HTMLInputElement>('[data-role="q"]')?.focus();
    return;
  }
  // Number keys pick a section, not a view: the sections are the fixed thing
  // in the rail, and the views inside one are a different kind of choice.
  const n = Number(e.key);
  if (n >= 1 && n <= SECTIONS.length) {
    const sec = SECTIONS[n - 1]!;
    e.preventDefault();
    store.go(sec.views[0]!, sec.name === "people" ? { personId: null } : {});
  }
});

// --- boot -----------------------------------------------------------------

store.on((what) => {
  // A name is new evidence. The server has already offered back the
  // photographs holding faces nobody has placed; this goes and looks at them.
  //
  // Stop meant "stop the pass you started on your own". Naming somebody is
  // asking for exactly this work, so it lifts that.
  if (what === "named") {
    sweepDeclined = false;
    peopleCorrected();
  }
  if (what === "filter") {
    syncFilters();
    syncNarrowed();
    void refreshPeopleBar();
    current?.refresh();
  } else if (what === "route") {
    syncNav();
    mountView();
  } else if (what === "layout") {
    syncNav();
    if (store.route.view === "cards") current?.refresh();
  } else if (what === "library") {
    void boot();
  }
});

window.addEventListener("heirloom:process-people", () => void runProcessing());
window.addEventListener("heirloom:asset-edited", () => current?.refresh({ quiet: true }));

async function loadStats(): Promise<void> {
  try {
    store.stats = await api.stats(store.libraryId);
  } catch (e) {
    store.stats = null;
    toast(e instanceof Error ? e.message : "could not read this library", true);
  }
}

/**
 * After a successful sign-in there is a cookie, so start again from scratch.
 *
 * A brand new account is carried across in the URL rather than in a variable,
 * because the reload throws every variable away — and somebody who has just
 * made an archive should be told they have one, not dropped into an empty
 * grid that looks like a page that failed to load.
 */
function signedIn(created: boolean): void {
  const url = new URL(location.href);
  url.searchParams.delete("screen");
  if (created) url.searchParams.set("welcome", "1");
  location.replace(url.toString());
}

async function boot(): Promise<void> {
  const wanted = store.adopt();

  // `?demo=1&screen=login` renders the sign-in screen against the fixtures, so
  // it can be looked at without a Worker to sign in to. Signing in drops the
  // parameter rather than reloading back into the same screen.
  if (new URLSearchParams(location.search).get("screen") === "login") {
    replace(root, loginScreen(signedIn));
    return;
  }

  replace(root, h("div", { class: "centre" }, h("div", { class: "loading", text: "· · ·  opening the archive" })));

  try {
    store.me = await api.me();
  } catch (e) {
    replace(root, loginScreen(signedIn));
    if (e instanceof Error && !/401|sign/i.test(e.message)) toast(e.message, true);
    return;
  }

  const libs = store.me.libraries;
  if (!libs.length) {
    replace(
      root,
      h(
        "div",
        { class: "centre" },
        h(
          "div",
          { class: "empty" },
          h("h3", { text: "No library yet" }),
          h("p", {
            text: "This account is not a member of any archive. Open the desktop app to make one, or ask whoever runs the family library to invite this address.",
          }),
        ),
      ),
    );
    return;
  }

  let stored: string | null = null;
  try {
    stored = localStorage.getItem("heirloom.library");
  } catch {
    /* ignore */
  }
  const pick =
    [wanted, stored].find((id) => id && libs.some((l) => l.id === id)) ?? libs[0]!.id;
  store.libraryId = pick;
  try {
    localStorage.setItem("heirloom.library", pick);
  } catch {
    /* ignore */
  }

  sweepDeclined = false;
  peopleCache = null;
  wireDrop();
  await loadStats();
  shell();
  connectLive(pick);

  // A brand new archive, made moments ago by entering a code. Told, and
  // pointed at the one thing to do next, because an empty grid on a first
  // visit looks like a page that failed rather than a beginning.
  const fresh = new URLSearchParams(location.search).get("welcome") === "1";
  if (fresh) {
    const url = new URL(location.href);
    url.searchParams.delete("welcome");
    history.replaceState(null, "", url.toString());
    toast("Your archive is ready. Add some photographs and it will do the rest.");
  } else if (store.stats && store.stats.assets === 0) {
    toast(`${store.stats.library.name} is empty — ${count(0, "photograph")} so far.`);
  }

  // Anything waiting, including a pass somebody interrupted by closing the
  // tab. Not awaited: opening the archive should not wait on a model.
  void sweep();
}

window.addEventListener("hashchange", () => {
  const before = `${store.route.view}/${store.route.personId}/${store.route.treeRoot}`;
  store.adopt();
  const after = `${store.route.view}/${store.route.personId}/${store.route.treeRoot}`;
  if (before !== after) mountView();
  syncFilters();
  syncNarrowed();
});

void boot();
